import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "./Navigation.jsx";
import { wsRequest, onBroadcast } from "./wsClient.js";
import { useTheme } from "./ThemeContext.jsx";
import { onLoginRequested, onAuthChanged, announceAuthChange } from "./authPrompt.js";
import AuthForm from "./AuthForm.jsx";
import { showToast } from "./toast.js";
import { siteDateTime } from "./siteTime.js";

const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend
const USER_TOKEN_KEY = "589comps_user_token";

// Site-wide header: logo, name, nav (About/FAQ), and a dropdown on the
// right that's self-contained for auth — it manages its own token/user
// state (reading/writing the same localStorage key every page uses), so it
// works standalone on any page. Rendered once, globally, in main.jsx (not
// per-page) so it never unmounts/remounts on in-app navigation. Pages that
// need to react to login/logout (the buyer page, for guessing/purchasing)
// subscribe to onAuthChanged from authPrompt.js — this broadcasts on
// every auth resolution (initial token verification, login, logout),
// regardless of which page happens to be active at the time.
const SITE_NOTIF_SEEN_KEY = "589comps_notif_seen_at";
const SITE_NOTIF_CLEARED_KEY = "589comps_notif_cleared_at";

// Site-wide notification bell — public announcements (a competition
// starting, a competition being won), shown to anyone browsing regardless
// of login state. Distinct from the personal alerts list under "Logged in
// as" further down this file. "Seen"/"cleared" are both tracked in
// localStorage rather than on the account, since this needs to work for
// logged-out visitors too — and since the underlying list is shared
// site-wide, "clear" only dismisses it in this browser, not for everyone
// else looking at the same feed.
// SOONEST FIRST. `competitions.list` comes back in creation order, which
// has nothing to do with when a competition starts once anything has been
// rescheduled — so a buyer could be shown the last competition in the
// queue above the one starting next.
//
// THIRD PLACE THIS SAME MISTAKE APPEARED: the ended-branch "next
// competition", the nothing-has-run-yet branch, and here. Extracted so
// the two fetches below cannot disagree with each other.
//
// Unscheduled competitions sort last: they have no start to be near.
// THE SAME MARKER THE ACCOUNT WIDGET USES (1.483.0), so a partner's
// visitor and this site's own see the same thing in the same words.
//
// The badge says how many; until now nothing said WHICH, so a reader
// with three unread had to re-read the whole list to find them. Raised
// while syncing the widget's Alerts section against this menu.
function NewTag() {
  return (
    <span
      style={{
        fontSize: 11,
        marginLeft: 6,
        padding: "1px 6px",
        borderRadius: 10,
        border: "1px solid var(--accent-blue)",
        color: "var(--accent-blue)",
        verticalAlign: "middle",
      }}
    >
      New
    </span>
  );
}

function queuedSoonestFirst(list) {
  return (list || [])
    // LOCKED ONLY. An unlocked competition's date can still move and it
    // may never run at all — nothing about it is certain, so showing it
    // to a buyer announces something the site has not committed to.
    //
    // The same rule the countdown follows: state a time only when it can
    // no longer change. It also means everything in this list is
    // confirmed by definition, so the per-row "Confirmed" badge is gone.
    .filter((c) => c.status === "queued" && c.lockedIn)
    .sort((a, b) => {
      const at = a.startAt ?? a.scheduledStartAt;
      const bt = b.startAt ?? b.scheduledStartAt;
      if (at == null && bt == null) return 0;
      if (at == null) return 1;
      if (bt == null) return -1;
      return at - bt;
    });
}

// `token` is a prop because the bell now asks for THIS MODE's
// notifications, and the server reads the mode from the token. It took
// none before, which is how a reference to an undeclared `token` got past
// review and blanked the page on 2 Sep.
function NotificationBell({ token }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => Number(localStorage.getItem(SITE_NOTIF_SEEN_KEY)) || 0);
  const [clearedAt, setClearedAt] = useState(() => Number(localStorage.getItem(SITE_NOTIF_CLEARED_KEY)) || 0);
  const containerRef = useRef(null);

  const refresh = () => {
    // The token, so the bell shows THIS mode's notifications. Without it
      // the server resolves to Live and a Test site lists the real site's
      // competitions starting — the same omission competitions.list had.
      wsRequest("notifications.list", {}, token)
      .then(setNotifications)
      .catch(() => {});
  };

  useEffect(refresh, []);
  useEffect(
    () =>
      onBroadcast((message) => {
        if (message.type === "site-notifications-changed") refresh();
      }),
    []
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const visibleNotifications = notifications.filter((n) => n.createdAt > clearedAt);
  const unreadCount = visibleNotifications.filter((n) => n.createdAt > seenAt).length;

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const now = Date.now();
      setSeenAt(now);
      localStorage.setItem(SITE_NOTIF_SEEN_KEY, String(now));
    }
  };

  const clearAll = () => {
    const now = Date.now();
    setClearedAt(now);
    localStorage.setItem(SITE_NOTIF_CLEARED_KEY, String(now));
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        onClick={toggleOpen}
        title="Notifications"
        style={{
          background: "var(--bg-panel-alt)",
          border: "1px solid var(--border-color-light)",
          borderRadius: 6,
          color: "var(--text-primary)",
          padding: "8px 10px",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              background: "#7ab8ff",
              color: "#111",
              borderRadius: 10,
              padding: "1px 6px",
              lineHeight: 1.4,
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          className="header-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-color)",
            borderRadius: 8,
            padding: 12,
            maxHeight: "60vh",
            overflowY: "auto",
            zIndex: 30,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            textAlign: "left",
          }}
        >
          {visibleNotifications.length === 0 ? (
            <p style={{ fontSize: 13, opacity: 0.6, margin: 0 }}>No notifications yet.</p>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <button
                  onClick={clearAll}
                  style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Clear
                </button>
              </div>
              {[...visibleNotifications]
                .reverse()
                .map((n) => (
                  <div key={n.id} style={{ display: "flex", gap: 8, fontSize: 13, marginBottom: 10 }}>
                    <span>
                        {/* Every type except "won" shared one icon, so a
                            competition being CONFIRMED looked identical to
                            one STARTING — which is most of what this list
                            now carries. */}
                        {n.type === "won"
                          ? "🏆"
                          : n.type === "locked"
                            ? "📌"
                            : n.type === "started"
                              ? "🎉"
                              : n.type === "missed"
                                ? "⚠️"
                                : "🔔"}
                      </span>
                    <div>
                      <div>{n.message}</div>
                      <div style={{ fontSize: 11, opacity: 0.55 }}>{siteDateTime(n.createdAt)}</div>
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SiteHeader() {
  const navigate = useNavigate();
  const { theme, toggleTheme, applyAccountTheme } = useTheme();
  const [siteSettings, setSiteSettings] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(USER_TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => onLoginRequested(() => setOpen(true)), []);

  // AuthForm (used here and in any "you need to be logged in" modal
  // elsewhere) broadcasts here the moment a login/signup succeeds,
  // regardless of which particular instance the user actually went
  // through — this is what keeps the header in sync even when someone
  // logs in via a modal on the competition page rather than this dropdown.
  useEffect(
    () =>
      onAuthChanged((u, t) => {
        setUser(u);
        setToken(t);
        applyAccountTheme(u?.themePreference);
        // Only close on an actual successful login/signup, not on every
        // auth change — this same broadcast also fires for a logout/
        // session-clear (see clearSession below), which needs the
        // dropdown to stay open so the login form it opened is actually
        // visible, not immediately closed again by this same listener.
        if (u) setOpen(false);
      }),
    []
  );

  const [upcoming, setUpcoming] = useState(null);
  const [results, setResults] = useState(null);
  const [payoutAlerts, setPayoutAlerts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Root-managed nav links (About, FAQ, or anything else root's added
  // as a content page with its placement set to "header") — same
  // fetch-on-mount, refresh-on-broadcast pattern Footer.jsx already
  // uses for its own legal-document links.
  const [headerPages, setHeaderPages] = useState([]);

  // WHETHER WINNERS ARE PUBLIC, for the nav item.
  //
  // Fetched here rather than taken as a prop, because this component
  // already fetches its own content pages and has no config passed in —
  // adding a prop would mean threading it through every caller.
  //
  // Defaults to FALSE until the answer arrives, so the link does not
  // flash into view on a site that has winners switched off.
  const [winnersPublic, setWinnersPublic] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setWinnersPublic(!!data.winnersPublic);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const loadHeaderPages = () => {
    wsRequest("content.list")
      .then((list) => setHeaderPages(list.filter((p) => p.inHeader)))
      .catch(() => {});
  };
  useEffect(loadHeaderPages, []);
  useEffect(() => onBroadcast((message) => {
    if (message.type === "content-pages-changed") loadHeaderPages();
  }), []);

  const containerRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // API-exclusive mode: this site is reachable only through a partner's
  // own integration, so it shouldn't present itself as somewhere to use
  // an account. A session that predates the setting being switched on
  // stays perfectly valid — and must, since a partner's linked users
  // hold exactly that kind of session and use it through the partner — but
  // this site stops offering account UI for it.
  //
  // Logout is the deliberate exception below: someone left holding a
  // stale session needs a way to clear it, and hiding every other
  // control while hiding that one too would strand them.
  const apiExclusive = !!siteSettings?.apiExclusive;

  const loadSiteSettings = () => {
    wsRequest("siteSettings.get")
      .then((data) => setSiteSettings(data))
      .catch(() => {});
  };

  useEffect(loadSiteSettings, []);

  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "site-settings-changed") loadSiteSettings();
    });
  }, []);

  // Resolve/verify whoever's logged in, on mount and whenever the token
  // changes — mirrors what the old per-page AuthWidget did, just
  // centralized here now since every page shares this one header.
  // Broadcasts via announceAuthChange rather than a prop callback, so any
  // page that needs to know the current user (App.jsx does, to enable
  // guessing/purchasing) can subscribe via onAuthChanged regardless of
  // whether this header instance was rendered by that specific page —
  // this header is now rendered once, globally, in main.jsx.

  // Shared by both the ordinary 401-on-next-request path below and the
  // real-time push listener further down — same end state either way:
  // local session cleared, every subscriber (App.jsx etc.) told nobody's
  // logged in anymore, and the login form opened so there's no need to
  // hunt for it. message is shown as a toast when provided, explaining
  // why (rather than just silently logging the person out).
  const clearSession = (message) => {
    localStorage.removeItem(USER_TOKEN_KEY);
    setToken(null);
    setUser(null);
    announceAuthChange(null, null);
    setOpen(true);
    // The dropdown lives in the header — if the person had scrolled down
    // the page, it would open off-screen and be just as easy to miss as
    // if it hadn't opened at all.
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (message) showToast(message, "error");
  };

  // Shared by the effect below (token just became known/changed) and the
  // profile-updated listener further down (same account's data changed in
  // a different tab) — both need the exact same "fetch the current user,
  // tell everyone, apply their theme" sequence.
  const refreshCurrentUser = (currentToken) => {
    wsRequest("auth.me", {}, currentToken)
      .then((u) => {
        setUser(u);
        announceAuthChange(u, currentToken);
        applyAccountTheme(u.themePreference);
      })
      .catch((err) => {
        // Only clear the session on a confirmed auth failure — a network
        // hiccup or a WS reconnect-in-progress shouldn't log anyone out.
        if (err.status === 401 || err.status === 403) clearSession(err.message);
      });
  };

  useEffect(() => {
    if (!token) {
      announceAuthChange(null, null);
      return;
    }
    refreshCurrentUser(token);
  }, [token]);

  // Real-time push from the backend the instant a newer login elsewhere
  // supersedes this session (see issueToken/notifySessionSuperseded on the
  // backend) — without this, a tab sitting idle wouldn't find out until
  // its next request happened to fail, which could be a while (or never,
  // if the person never triggers one). This is what makes it immediate:
  // no refresh, no waiting for the next auth.me poll, logged out and
  // prompted to log back in the moment it actually happens.
  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "session-superseded") clearSession(message.message);
    });
  }, []);

  // Real-time push the instant this account's profile data changes in a
  // different tab/device (see updateMe/notifyProfileUpdated on the
  // backend) — e.g. saving a spend preference in one tab while another
  // tab on the same account is already open elsewhere. The in-memory
  // announceAuthChange pub/sub other components (App.jsx included) rely
  // on is per-tab only and has no way to reach across separate tabs on
  // its own; re-fetching and re-announcing here is what actually closes
  // that gap, since every subscriber just needs the current user pushed
  // through the same channel it already listens to, regardless of which
  // tab's save triggered it.
  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "profile-updated" && token) refreshCurrentUser(token);
    });
  }, [token]);

  // Runs whenever logged in, independent of whether the dropdown is open —
  // this is what lets a badge show on the toggle button itself, not just
  // inside the opened dropdown. Also re-runs on every "competitions-changed"
  // broadcast — winning a competition doesn't change `user`/`token`, so
  // without this the badge/results would only ever catch up on a full page
  // reload (which remounts the component and re-runs this from scratch).
  const loadAlertsData = () => {
    if (!user) {
      setPayoutAlerts([]);
      return;
    }
    wsRequest("auth.payoutAlerts", {}, token)
      .then(setPayoutAlerts)
      .catch(() => {});
    wsRequest("auth.myResults", {}, token)
      .then(setResults)
      .catch(() => {});
  };

  useEffect(loadAlertsData, [user, token]);

  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "competitions-changed") {
        loadAlertsData();
        // AND THE UPCOMING LIST, which the badge counts from.
        //
        // This reloaded the alerts only, so a competition locking lit the
        // bell immediately — that list has its own handler — while the
        // account badge sat unchanged until the page was reloaded.
        // Reported 2 Sep.
        wsRequest("competitions.list", {}, token)
          .then((list) => setUpcoming(queuedSoonestFirst(list)))
          .catch(() => {});
      }
    });
  }, [user, token]);

  // Close the dropdown on an outside click — standard dropdown behavior.
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Same behavior for the mobile hamburger menu.
  useEffect(() => {
    const onDocClick = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // THE UPCOMING LIST IS NEEDED BEFORE THE MENU OPENS, now that the badge
  // counts newly-confirmed competitions.
  //
  // It used to load only on open, on the reasoning that there was no point
  // fetching it in the background. There is now: a badge that only appears
  // once you have opened the menu is a badge that never appears, because
  // opening the menu is what marks everything seen.
  //
  // Once per mount for a logged-in visitor, and again on open so it is
  // fresh when actually read.
  useEffect(() => {
    if (!user) return;
    wsRequest("competitions.list", {}, token)
      .then((list) => setUpcoming(queuedSoonestFirst(list)))
      .catch(() => setUpcoming([]));
  }, [user, token]);

  // The rest of the panel's content still loads only when opened — none of
  // it feeds a badge, so fetching it in the background would buy nothing.
  // WHAT WAS UNREAD AT THE MOMENT THIS WAS OPENED (1.483.0).
  //
  // The per-row "New" markers cannot read user.alertsSeenAt: opening the
  // menu moves that forward (below), so every marker would appear and
  // vanish in the same frame. This is the value as it was on opening,
  // taken before markAlertsSeen resolves, and it stays put until the
  // menu is closed and opened again — which is what a reader expects
  // "new since you last looked" to mean.
  const [seenOnOpen, setSeenOnOpen] = useState(0);
  useEffect(() => {
    if (open) setSeenOnOpen(user?.alertsSeenAt || 0);
    // Deliberately NOT depending on user: it is updated by the very
    // request this snapshot exists to outlive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !user) return;
    // The token, so the list is scoped to the mode this person is
      // actually looking at. Without it the server resolves to Live and a
      // Test site says "Nothing queued right now" however much is queued.
      wsRequest("competitions.list", {}, token)
      .then((list) => setUpcoming(queuedSoonestFirst(list)))
      .catch(() => setUpcoming([]));
    // Opening the dropdown is "reading" the alerts — persisted on the
    // account server-side (not localStorage), so the unread badge stays
    // correct across devices/browsers, not just this one.
    wsRequest("auth.markAlertsSeen", {}, token)
      .then((data) => setUser((prev) => (prev ? { ...prev, alertsSeenAt: data.alertsSeenAt } : prev)))
      .catch(() => {});
  }, [open, user, token]);

  const clearAlerts = async () => {
    try {
      const data = await wsRequest("auth.clearAlerts", {}, token);
      setUser((prev) => (prev ? { ...prev, alertsSeenAt: data.alertsSeenAt } : prev));
    } catch {
      // ignore — worst case the list doesn't persist as cleared until retried
    }
    setResults([]);
  };

  // THE MENU FLAGS WHAT IS INSIDE IT.
  //
  // Two kinds of news live in this dropdown: things that happened to YOU
  // (a competition won, a prize owed), and competitions that have been
  // CONFIRMED since you last looked — their start time can no longer move,
  // which is the moment it becomes worth planning around.
  //
  // Both are measured against the same alertsSeenAt, which opening the
  // menu updates, so the badge clears the way a reader expects.
  //
  // Only newly-locked ones count. A competition that locked days ago is
  // not news, and a queue full of long-confirmed competitions must not
  // leave the badge permanently lit.
  const seenAt = user?.alertsSeenAt || 0;
  const unreadResults = (results || []).filter((r) => r.alertAt > seenAt).length;
  const unreadConfirmed = (upcoming || []).filter(
    (c) => c.lockedAt && c.lockedAt > seenAt
  ).length;
  const unreadCount = unreadResults + unreadConfirmed;

  const logout = async () => {
    try {
      await wsRequest("auth.logout", {}, token);
    } catch {
      // ignore
    }
    localStorage.removeItem(USER_TOKEN_KEY);
    setToken(null);
    setUser(null);
    setOpen(false);
    announceAuthChange(null, null);
    // LEAVE THE PAGE, not just the session.
    //
    // Clearing the token stops further requests but does nothing about
    // what is already rendered: log out from /profile and the previous
    // person's name, email, address and payment history stay on screen
    // until somebody navigates. On a shared machine that is the whole
    // point of logging out.
    //
    // Home rather than a dedicated "logged out" page: it is a real page
    // that works signed out, so there is nothing to build and nothing to
    // get stale.
    navigate("/");
  };

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 20px",
        borderBottom: "1px solid var(--border-color)",
        marginBottom: 20,
      }}
    >
      <NavLink to="/" style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-primary)", textDecoration: "none" }}>
        {siteSettings?.hasLogo && (
          <img
            src={`${API_BASE}/api/site-logo/square`}
            alt=""
            style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", display: "block" }}
          />
        )}
        <span style={{ fontSize: 18, fontWeight: 700 }}>{siteSettings ? siteSettings.siteName : ""}</span>
      </NavLink>

      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <nav className="header-desktop-nav" style={{ gap: 18 }}>
          {/* WINNERS — a route of this site's own, not a page an
              operator wrote, so it is not in headerPages and is listed
              before them.

              HIDDEN WHEN SWITCHED OFF. An operator can turn the public
              winners list off entirely, and a nav item pointing at a
              page that 404s is worse than no nav item. */}
          {winnersPublic && (
            <NavLink to="/winners" style={{ color: "var(--text-secondary)" }}>
              Winners
            </NavLink>
          )}
          {headerPages.map((p) => (
            <NavLink key={p.slug} to={p.url} style={{ color: "var(--text-secondary)", fontSize: 14 }}>
              {p.title}
            </NavLink>
          ))}
        </nav>

        {!apiExclusive && <NotificationBell token={token} />}

        <div className="header-desktop-only" style={{ gap: 8, alignItems: "center" }}>
          <button
            onClick={() => toggleTheme(token)}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              background: "var(--bg-panel-alt)",
              border: "1px solid var(--border-color-light)",
              borderRadius: 6,
              color: "var(--text-primary)",
              padding: "8px 10px",
              cursor: "pointer",
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
          {user && (
            <button
              onClick={logout}
              style={{
                fontSize: 13,
                background: "none",
                border: "1px solid var(--border-color-light)",
                borderRadius: 6,
                color: "var(--text-secondary)",
                padding: "8px 10px",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          )}
        </div>

        {!apiExclusive && (
        <div ref={containerRef} style={{ position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => setOpen(!open)}
              style={{
                background: "var(--bg-panel-alt)",
                border: "1px solid var(--border-color-light)",
                borderRadius: 6,
                color: "var(--text-primary)",
                padding: "8px 14px",
                cursor: "pointer",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {user ? `Logged in as ${user.username || user.email}` : "Login / Register"}
              {unreadCount > 0 && (
                <span
                  title={`${unreadCount} unread alert${unreadCount === 1 ? "" : "s"}`}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    background: "#7ab8ff",
                    color: "#111",
                    borderRadius: 10,
                    padding: "1px 6px",
                    lineHeight: 1.4,
                  }}
                >
                  {unreadCount}
                </span>
              )}
              {payoutAlerts.length > 0 && (
                <span
                  title="Payment details needed"
                  style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent-yellow)", display: "inline-block" }}
                />
              )}
              <span style={{ fontSize: 10 }}>▼</span>
            </button>
          </div>

          {open && (
            <div
              className="header-dropdown"
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                background: "var(--bg-panel)",
                border: "1px solid var(--border-color)",
                borderRadius: 8,
                padding: 16,
                maxHeight: "70vh",
                overflowY: "auto",
                zIndex: 30,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                textAlign: "left",
              }}
            >
              {/* No apiExclusive branch here anymore: the whole dropdown
                  is hidden in that mode, so this can only ever render
                  when accounts are genuinely usable on this site. An
                  earlier version showed a "managed through the partner's
                  own site" note in place of the form — unreachable once
                  the button that opens this stopped rendering, and dead
                  code explaining live behaviour is worse than none. */}
              {!user ? (
                <AuthForm />
              ) : (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                    <NavLink to="/profile/account" style={{ fontSize: 13, color: "var(--accent-blue)" }}>
                      My account →
                    </NavLink>
                    {results && results.length > 0 && (
                      <button
                        onClick={clearAlerts}
                        style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                      >
                        Clear Alerts
                      </button>
                    )}
                  </div>

                  {payoutAlerts.length > 0 && (
                    <div
                      style={{
                        background: "var(--warning-bg)",
                        border: "1px solid var(--accent-yellow)",
                        borderRadius: 6,
                        padding: 10,
                        marginBottom: 14,
                        fontSize: 13,
                        color: "var(--accent-yellow)",
                      }}
                    >
                      An admin needs your payment details for{" "}
                      {payoutAlerts.length === 1 ? `"${payoutAlerts[0].title}"` : `${payoutAlerts.length} prizes you've won`}.{" "}
                      <NavLink to="/profile/payout" style={{ color: "var(--accent-yellow)", textDecoration: "underline" }}>
                        Set them up →
                      </NavLink>
                    </div>
                  )}

                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Upcoming competitions</div>
                  {upcoming === null ? (
                    <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
                  ) : upcoming.length === 0 ? (
                    <p style={{ fontSize: 13, opacity: 0.6 }}>
                      {/* NOT "nothing queued" — competitions may well be
                          queued and simply not confirmed yet, and telling
                          somebody there is nothing coming would be wrong.
                          This list only shows dates that can no longer
                          move. */}
                      No confirmed dates yet — check back soon.
                    </p>
                  ) : (
                    upcoming.map((c) => (
                      <div key={c.id} style={{ fontSize: 13, marginBottom: 8 }}>
                        <strong>{c.title}</strong>
                        {c.lockedAt > seenOnOpen && <NewTag />}
                        {/* NO "start time not yet known" FALLBACK any more
                            (1.483.0). It could not fire: lockInWindowFor
                            refuses to lock anything without a
                            scheduledStartAt, this list shows only locked
                            competitions, and startAt is computeEffectiveStart
                            — which returns that same value. Dead code
                            describing live behaviour is worse than none. */}
                        <div style={{ opacity: 0.6, fontSize: 12 }}>{siteDateTime(c.startAt)}</div>
                      </div>
                    ))
                  )}

                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginTop: 16,
                      marginBottom: 8,
                      borderTop: "1px solid var(--border-color)",
                      paddingTop: 12,
                    }}
                  >
                    Results for your competitions
                  </div>
                  {results === null ? (
                    <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
                  ) : results.length === 0 ? (
                    <p style={{ fontSize: 13, opacity: 0.6 }}>No results yet.</p>
                  ) : (
                    results.map((r) => (
                      <div key={r.competitionId} style={{ fontSize: 13, marginBottom: 8 }}>
                        <strong>{r.title}</strong>
                        {r.alertAt > seenOnOpen && <NewTag />}
                        <div style={{ fontSize: 12, color: r.wasMeTheWinner ? "var(--accent-green)" : "var(--text-secondary)" }}>
                          {r.wasMeTheWinner
                              ? "You won! 🎉"
                              : r.winnerUsername
                                ? `Won by ${r.winnerUsername}`
                                : /* NO WINNER RECORDED.
                                     This printed "Won by null". A competition can
                                     end without an answeredByUsername — a lottery
                                     draw with no eligible ticket, or a winner whose
                                     account has since gone.
                                     Deliberately NOT falling back to the winner's
                                     email, which some other payloads do: this panel
                                     is shown to everyone, and another buyer's email
                                     address is not theirs to see. Reported 6 Sep. */
                                  "Ended with no winner recorded"}
                        </div>
                        {r.wasMeTheWinner && r.prizePaid && (
                          <div style={{ fontSize: 12, color: "var(--accent-blue)" }}>💰 Prize paid</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        <div ref={mobileMenuRef} className="header-hamburger-button" style={{ position: "relative", alignItems: "center" }}>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
            style={{
              background: "var(--bg-panel-alt)",
              border: "1px solid var(--border-color-light)",
              borderRadius: 6,
              color: "var(--text-primary)",
              padding: "8px 10px",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ☰
          </button>
          {mobileMenuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                background: "var(--bg-panel)",
                border: "1px solid var(--border-color)",
                borderRadius: 8,
                padding: 8,
                minWidth: 140,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                zIndex: 20,
              }}
            >
              {/* WINNERS, in the mobile menu too — a nav item missing
                  from one of the two menus is worse than none, and so is
                  one that survives the switch being turned off. */}
              {winnersPublic && (
                <NavLink
                  to="/winners"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: "var(--text-primary)", fontSize: 14, padding: "10px 0" }}
                >
                  Winners
                </NavLink>
              )}
              {headerPages.map((p) => (
                <NavLink
                  key={p.slug}
                  to={p.url}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: "var(--text-primary)", fontSize: 14, padding: "8px 10px", borderRadius: 6, textAlign: "center", display: "block" }}
                >
                  {p.title}
                </NavLink>
              ))}
              {user && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  style={{
                    color: "var(--text-primary)",
                    fontSize: 14,
                    padding: "8px 10px",
                    borderRadius: 6,
                    textAlign: "center",
                    display: "block",
                    background: "none",
                    border: "none",
                    borderTop: "1px solid var(--border-color)",
                    marginTop: 4,
                    paddingTop: 12,
                    cursor: "pointer",
                  }}
                >
                  Logout
                </button>
              )}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  toggleTheme(token);
                }}
                style={{
                  color: "var(--text-primary)",
                  fontSize: 14,
                  padding: "8px 10px",
                  borderRadius: 6,
                  textAlign: "center",
                  display: "block",
                  background: "none",
                  border: "none",
                  ...(user ? {} : { borderTop: "1px solid var(--border-color)", marginTop: 4, paddingTop: 12 }),
                  cursor: "pointer",
                }}
              >
                {theme === "dark" ? "☀️ Light mode" : "🌙 Dark mode"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
